import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
    Database,
    ExternalLink,
    Globe,
    ArrowRight,
    Search,
    Handshake
} from 'lucide-react';
import { Input } from '../components/ui/input';

const partnerColors = [
    { bg: 'from-rose-600 to-rose-800', accent: 'bg-rose-100 text-rose-800 border-rose-200', card: 'hover:border-rose-300' },
    { bg: 'from-indigo-600 to-indigo-800', accent: 'bg-indigo-100 text-indigo-800 border-indigo-200', card: 'hover:border-indigo-300' },
    { bg: 'from-emerald-600 to-emerald-800', accent: 'bg-emerald-100 text-emerald-800 border-emerald-200', card: 'hover:border-emerald-300' },
    { bg: 'from-amber-600 to-amber-800', accent: 'bg-amber-100 text-amber-800 border-amber-200', card: 'hover:border-amber-300' },
    { bg: 'from-violet-600 to-violet-800', accent: 'bg-violet-100 text-violet-800 border-violet-200', card: 'hover:border-violet-300' },
];

export default function PartnerDatabasesPage() {
    const [partners, setPartners] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const fetchPartners = async () => {
            try {
                const res = await api.get('/partner-databases');
                setPartners(res.data);
            } catch (error) {
                console.error('Failed to load partners:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchPartners();
    }, []);

    const filteredPartners = partners.filter(p => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return (
            (p.name || '').toLowerCase().includes(q) ||
            (p.description || '').toLowerCase().includes(q) ||
            (p.organization || '').toLowerCase().includes(q) ||
            (p.databases || []).some(d =>
                (d.name || '').toLowerCase().includes(q) ||
                (d.description || '').toLowerCase().includes(q) ||
                (d.type || '').toLowerCase().includes(q)
            )
        );
    });

    return (
        <div className="min-h-screen bg-muted/30" data-testid="partner-databases-page">
            {/* Hero */}
            <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 text-white">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <Handshake className="h-10 w-10 text-teal-300" />
                    </div>
                    <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
                        Trusted Partner Databases
                    </h1>
                    <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
                        HAAPE Hub curates access to leading databases from our trusted partners — organizations 
                        at the forefront of neurodiversity research, employment, and industry innovation.
                    </p>
                    <div className="relative max-w-md mx-auto">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                        <Input
                            placeholder="Search partners, databases, topics..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-12 h-12 bg-white/10 border-white/20 text-white placeholder:text-slate-400 focus:bg-white/15"
                            data-testid="partner-search"
                        />
                    </div>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {loading ? (
                    <div className="space-y-8">
                        {[1, 2].map(i => <Skeleton key={i} className="h-64 rounded-2xl" />)}
                    </div>
                ) : filteredPartners.length === 0 ? (
                    <div className="text-center py-20 text-muted-foreground">
                        <Database className="h-12 w-12 mx-auto mb-4 opacity-40" />
                        <p className="text-lg font-medium">No partners found</p>
                        <p className="text-sm mt-1">Try a different search term</p>
                    </div>
                ) : (
                    <div className="space-y-10">
                        {filteredPartners.map((partner, idx) => {
                            const colors = partnerColors[idx % partnerColors.length];
                            return (
                                <Card
                                    key={partner.partner_id}
                                    className={`overflow-hidden border-2 transition-all duration-300 ${colors.card}`}
                                    data-testid={`partner-card-${partner.partner_id}`}
                                >
                                    {/* Partner Header */}
                                    <div className={`bg-gradient-to-r ${colors.bg} p-6 sm:p-8 text-white`}>
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-3 mb-1">
                                                    <Badge className="bg-white/20 text-white border-white/30 text-xs">
                                                        Trusted Partner
                                                    </Badge>
                                                </div>
                                                <h2 className="text-2xl sm:text-3xl font-bold mt-3">{partner.name}</h2>
                                                {partner.organization && partner.organization !== partner.name && (
                                                    <p className="text-white/70 text-sm mt-1">{partner.organization}</p>
                                                )}
                                                <p className="text-white/80 mt-3 text-sm sm:text-base leading-relaxed max-w-3xl">
                                                    {partner.description}
                                                </p>
                                            </div>
                                            {partner.logo_url && (
                                                <img
                                                    src={partner.logo_url}
                                                    alt={partner.name}
                                                    className="h-16 w-16 sm:h-20 sm:w-20 object-contain rounded-xl bg-white/10 p-2 shrink-0"
                                                />
                                            )}
                                        </div>
                                        {partner.website && (
                                            <a
                                                href={partner.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1.5 mt-4 text-sm text-white/70 hover:text-white transition-colors"
                                            >
                                                <Globe className="h-4 w-4" />
                                                {partner.website.replace('https://', '').replace('http://', '')}
                                            </a>
                                        )}
                                    </div>

                                    {/* Databases */}
                                    <CardContent className="p-6 sm:p-8">
                                        <p className="text-sm font-medium text-muted-foreground mb-4 uppercase tracking-wider">
                                            Available Databases ({(partner.databases || []).length})
                                        </p>
                                        <div className="grid sm:grid-cols-2 gap-4">
                                            {(partner.databases || []).map((db) => (
                                                <a
                                                    key={db.db_id}
                                                    href={db.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="group border-2 border-border rounded-xl p-5 hover:shadow-lg hover:border-primary/30 transition-all duration-200 block"
                                                    data-testid={`database-link-${db.db_id}`}
                                                >
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <Database className="h-4 w-4 text-primary shrink-0" />
                                                                <h3 className="font-semibold text-sm sm:text-base truncate group-hover:text-primary transition-colors">
                                                                    {db.name}
                                                                </h3>
                                                            </div>
                                                            {db.type && (
                                                                <Badge variant="outline" className={`text-xs mb-2 ${colors.accent}`}>
                                                                    {db.type}
                                                                </Badge>
                                                            )}
                                                            <p className="text-sm text-muted-foreground leading-relaxed">
                                                                {db.description}
                                                            </p>
                                                        </div>
                                                        <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0 mt-1" />
                                                    </div>
                                                    <div className="flex items-center gap-1 mt-3 text-xs text-primary/60 group-hover:text-primary transition-colors">
                                                        <ExternalLink className="h-3 w-3" />
                                                        Visit Database
                                                    </div>
                                                </a>
                                            ))}
                                        </div>
                                        <p className="text-xs text-muted-foreground mt-5 text-center italic">
                                            Data provided by {partner.name}. HAAPE Hub links to partner databases for community benefit.
                                        </p>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {/* Credit Footer */}
                <div className="mt-16 text-center">
                    <p className="text-sm text-muted-foreground">
                        Partner databases are maintained by their respective organizations. HAAPE Hub provides curated access to support the neurodivergent employment ecosystem.
                    </p>
                    <div className="mt-4">
                        <Link to="/">
                            <Button variant="ghost" size="sm">Back to Home</Button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
